/**
 * Typed fetch wrapper for the `@invoice/api` worker.
 *
 * Closes part of #41. All API calls flow through `apiFetch` so we have a
 * single place that:
 *
 *  - Builds the absolute URL (Vite dev proxy hides this in dev; production
 *    deployments hit the API directly via `VITE_API_BASE_URL`).
 *  - Sets `credentials: 'include'` (auth lives in HTTP-only cookies set by
 *    the API; without `include` the browser would not send the session).
 *  - Sets `Accept: application/json` and parses the response body once.
 *  - Maps non-2xx responses to a typed `ApiError` so callers can branch on
 *    the canonical `{ code, message, details? }` envelope from
 *    `@invoice/shared/errors`.
 *  - Optionally validates the success body with a Zod schema so a future
 *    drift between API and UI surfaces at the call site instead of three
 *    layers down in JSX.
 */
import type { z } from 'zod';

/**
 * Canonical error shape from the API (`@invoice/shared/errors`).
 * Duplicated as a structural type here (not imported as a value) because
 * we never need the runtime guard - the API guarantees this envelope and
 * we just surface it back to callers.
 */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

/**
 * Thrown by `apiFetch` for any non-2xx response. Callers can pattern-match
 * on `code` (e.g. `auth_error`, `forbidden`, `validation_error`) for
 * targeted UX (logout-on-401, toast-on-validation, etc.).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

interface ApiFetchOptions<TSchema extends z.ZodTypeAny | undefined = undefined>
  extends Omit<RequestInit, 'body' | 'credentials' | 'headers'> {
  /** Optional Zod schema. When passed, the parsed response is returned. */
  schema?: TSchema;
  /** Query-string params; values are coerced to strings via `URLSearchParams`. */
  searchParams?: Record<string, string | number | boolean | undefined>;
  /** JSON body; serialised with `JSON.stringify`. Mutually exclusive with `body`. */
  json?: unknown;
  /** Raw body when not JSON (multipart, etc.). */
  body?: BodyInit;
  headers?: HeadersInit;
  /** Optional AbortSignal to cancel the request (TanStack Query passes one). */
  signal?: AbortSignal;
}

/** Resolves the API base URL. Empty in dev (Vite proxies `/api`). */
function apiBaseUrl(): string {
  // reason: import.meta.env is typed via vite/client; cast is safe because
  // the env is validated at build time.
  const base = import.meta.env.VITE_API_BASE_URL;
  return typeof base === 'string' && base.length > 0 ? base.replace(/\/$/, '') : '';
}

/**
 * Build the URL for a route. Routes are passed *without* the `/api` prefix
 * (e.g. `/invoices`); we add the prefix here so callers do not have to
 * remember it. In production `VITE_API_BASE_URL` already points at the
 * worker host, so we skip the `/api` prefix in that case.
 */
function buildUrl(path: string, searchParams?: ApiFetchOptions['searchParams']): string {
  const base = apiBaseUrl();
  // In dev: VITE_API_BASE_URL is empty, so we hit the same origin and let
  // the Vite proxy forward `/api/*` to `http://localhost:8787`. In prod the
  // base URL points at the worker host, so the `/api` prefix is omitted.
  const fullPath = base.length > 0 ? `${base}${path}` : `/api${path}`;

  if (!searchParams) return fullPath;
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === undefined) continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs.length > 0 ? `${fullPath}?${qs}` : fullPath;
}

/**
 * Generic typed fetch helper. The return type is `z.infer<TSchema>` when a
 * schema is passed, otherwise `unknown` (callers narrow with their own
 * type).
 */
export async function apiFetch<TSchema extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options: ApiFetchOptions<TSchema> = {},
): Promise<TSchema extends z.ZodTypeAny ? z.infer<TSchema> : unknown> {
  const { schema, searchParams, json, body, headers, signal, ...rest } = options;

  const requestInit: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : body !== undefined ? { body } : {}),
    ...(signal ? { signal } : {}),
  };

  const res = await fetch(buildUrl(path, searchParams), requestInit);

  // 204 No Content: nothing to parse, return undefined-as-T.
  if (res.status === 204) {
    // reason: there's no body to validate; callers requesting 204 routes
    // know to ignore the resolved value.
    return undefined as TSchema extends z.ZodTypeAny ? z.infer<TSchema> : unknown;
  }

  // Read body once. We tolerate non-JSON 4xx/5xx (e.g. plain-text errors
  // from a misconfigured proxy) by falling back to a synthetic envelope.
  const text = await res.text();
  let parsedBody: unknown;
  if (text.length === 0) {
    parsedBody = undefined;
  } else {
    try {
      parsedBody = JSON.parse(text);
    } catch {
      parsedBody = { code: 'invalid_response', message: text };
    }
  }

  if (!res.ok) {
    const errBody = (parsedBody as ApiErrorBody | undefined) ?? {
      code: 'unknown_error',
      message: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, errBody);
  }

  if (schema) {
    return schema.parse(parsedBody) as TSchema extends z.ZodTypeAny ? z.infer<TSchema> : unknown;
  }
  return parsedBody as TSchema extends z.ZodTypeAny ? z.infer<TSchema> : unknown;
}
